"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediaVolumeSlider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const remotion_1 = require("remotion");
const icons_1 = require("./icons");
const use_hover_state_1 = require("./use-hover-state");
const use_element_size_1 = require("./utils/use-element-size");
const BAR_HEIGHT = 5;
const KNOB_SIZE = 12;
const VERTICAL_PADDING = 4;
const VOLUME_SLIDER_WIDTH = 100;
const parentDivStyle = {
    display: 'inline-flex',
    background: 'none',
    border: 'none',
    padding: '6px',
    justifyContent: 'center',
    alignItems: 'center',
};
const containerStyle = {
    userSelect: 'none',
    paddingTop: VERTICAL_PADDING,
    paddingBottom: VERTICAL_PADDING,
    boxSizing: 'border-box',
    cursor: 'pointer',
    position: 'relative',
};
const barBackground = {
    height: BAR_HEIGHT,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    width: VOLUME_SLIDER_WIDTH,
    borderRadius: BAR_HEIGHT / 2,
};
const getVolumeFromX = (clientX, width) => {
    const pos = clientX;
    const volume = width === 0
        ? 0
        : remotion_1.interpolate(pos, [0, width], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
        });
    return volume;
};
const xSpacer = {
    width: 5,
};
const volumeContainer = {
    display: 'inline',
    width: icons_1.ICON_SIZE,
    height: icons_1.ICON_SIZE,
    cursor: 'pointer',
};
const MediaVolumeSlider = () => {
    const [mediaMuted, setMediaMuted] = remotion_1.Internals.useMediaMutedState();
    const [mediaVolume, setMediaVolume] = remotion_1.Internals.useMediaVolumeState();
    const [dragging, setDragging] = react_1.useState(false);
    const currentRef = react_1.useRef(null);
    const iconDivRef = react_1.useRef(null);
    const parentDivRef = react_1.useRef(null);
    const size = use_element_size_1.useElementSize(currentRef);
    const hover = use_hover_state_1.useHoverState(parentDivRef);
    const hoverOrDragging = hover || dragging;
    const onClick = react_1.useCallback(() => {
        if (mediaVolume === 0) {
            setMediaVolume(1);
            setMediaMuted(false);
            return;
        }
        setMediaMuted((mute) => !mute);
    }, [mediaVolume, setMediaMuted, setMediaVolume]);
    const onPointerDown = react_1.useCallback((e) => {
        if (!size) {
            throw new Error('Player has no size');
        }
        const _volume = getVolumeFromX(e.clientX - size.left - KNOB_SIZE / 2, size.width - KNOB_SIZE);
        setMediaVolume(_volume);
        if (_volume > 0) {
            setMediaMuted(false);
        }
        setDragging(true);
    }, [setMediaMuted, setMediaVolume, size]);
    const onPointerMove = react_1.useCallback((e) => {
        if (!size) {
            throw new Error('Player has no size');
        }
        if (!dragging)
            return;
        const _volume = getVolumeFromX(e.clientX - size.left - KNOB_SIZE / 2, size.width - KNOB_SIZE);
        setMediaVolume(_volume);
        if (_volume > 0) {
            setMediaMuted(false);
        }
    }, [dragging, setMediaMuted, setMediaVolume, size]);
    const onPointerUp = react_1.useCallback(() => {
        setDragging(false);
    }, []);
    react_1.useEffect(() => {
        if (!dragging) {
            return;
        }
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        };
    }, [currentRef, dragging, onPointerMove, onPointerUp, onPointerDown]);
    const knobStyle = react_1.useMemo(() => {
        var _a;
        return {
            height: KNOB_SIZE,
            width: KNOB_SIZE,
            borderRadius: KNOB_SIZE / 2,
            position: 'absolute',
            top: VERTICAL_PADDING - KNOB_SIZE / 2 + 5 / 2,
            backgroundColor: 'white',
            left: mediaMuted
                ? 0
                : Math.max(0, mediaVolume * (((_a = size === null || size === void 0 ? void 0 : size.width) !== null && _a !== void 0 ? _a : 0) - KNOB_SIZE)),
            boxShadow: '0 0 2px black',
            opacity: Number(hoverOrDragging),
        };
    }, [hoverOrDragging, mediaMuted, mediaVolume, size === null || size === void 0 ? void 0 : size.width]);
    const fillStyle = react_1.useMemo(() => {
        return {
            height: BAR_HEIGHT,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            width: mediaMuted ? 0 : (mediaVolume / 1) * 100 + '%',
            borderRadius: BAR_HEIGHT / 2,
        };
    }, [mediaMuted, mediaVolume]);
    const isMutedOrZero = mediaMuted || mediaVolume === 0;
    return (jsx_runtime_1.jsxs("div", Object.assign({ ref: parentDivRef, style: parentDivStyle }, { children: [jsx_runtime_1.jsx("div", Object.assign({ ref: iconDivRef, role: "button", "aria-label": isMutedOrZero ? 'Unmute sound' : 'Mute sound', title: isMutedOrZero ? 'Unmute sound' : 'Mute sound', onClick: onClick, style: volumeContainer }, { children: isMutedOrZero ? jsx_runtime_1.jsx(icons_1.VolumeOffIcon, {}, void 0) : jsx_runtime_1.jsx(icons_1.VolumeOnIcon, {}, void 0) }), void 0),
            jsx_runtime_1.jsx("div", { style: xSpacer }, void 0),
            jsx_runtime_1.jsx("div", Object.assign({ ref: currentRef, onPointerDown: onPointerDown, style: containerStyle }, { children: hoverOrDragging ? (jsx_runtime_1.jsxs(jsx_runtime_1.Fragment, { children: [jsx_runtime_1.jsx("div", Object.assign({ style: barBackground }, { children: jsx_runtime_1.jsx("div", { style: fillStyle }, void 0) }), void 0),
                        jsx_runtime_1.jsx("div", { style: knobStyle }, void 0)] }, void 0)) : null }), void 0)] }), void 0));
};
exports.MediaVolumeSlider = MediaVolumeSlider;
//# sourceMappingURL=MediaVolumeSlider.js.map